import { useEffect, useMemo, useRef, useState } from 'react';
import {
  hiringLinksAPI,
  type HiringPeopleSearch,
  type HiringPersonOption,
  type HiringRaiseBudget,
  type HiringRaiseCity,
  type HiringStoreOption,
  type HiringStoreSearch,
} from '../../services/api';
import { HiringSelect, type HiringSelectOption, type PickerTagView } from './HiringSelect';
import {
  MOVE_WORDS,
  RAISE_FORM,
  RAISE_WORDS,
  budgetOptionWords,
  peopleEmptyText,
  peopleFooter,
  personBlockedOf,
  personMeta,
  personName,
  personTagOf,
  raiseErrorText,
  storeOptionWords,
  storesFooter,
} from './hiringText';

/**
 * The four pickers of the Raise hiring window (15 Oct 2026), each the house's one-line
 * searchable select (HiringSelect):
 *
 *   StorePicker   the stores within this person's reach, "No store" first; a store a raise
 *                 would refuse is greyed with the server's reason on hover. The ChannelPlex
 *                 database only: no 1Office call. For "Move to another store" (mode 'move')
 *                 there is no "No store", and the store the opening is followed for now is
 *                 greyed ("Here now"); the move's review says why any other store is refused.
 *   PersonPicker  "Who". For a chosen store (15 Oct 2026): that store's people and nobody
 *                 else - deployed there now, or left it in the last 30 days - typed (2
 *                 letters), the same people by name or code. With no store: opened, the
 *                 Leaving list and then everyone else; typed, the project's people. One line
 *                 each: name · code · designation · store, and one tag. People are employees,
 *                 never candidates.
 *                 What is typed goes to the server only as the search, never into the page's
 *                 address or a log.
 *   BudgetSelect  every active budget: Store-level ones tagged (first for a store), the
 *                 replaced person's own first, "56 free of 147", Full ones greyed.
 *   CitySelect    Recruitment's own cities, searched by 2 letters: every one that matches.
 *
 * NOTHING IS CUT OFF (15 Oct 2026, the product owner). The stores and the people are read from
 * the server a page (50) at a time: scrolled to its end, a list asks for the next page, and so
 * on until the server says there is no more (usePagedRead). The budgets and the cities are
 * drawn in full. The only limits left are on what is picked or typed.
 *
 * A refusal the window acts on (hiring or raising switched off) goes to `onRefused` first:
 * it returns true when it did something about it.
 */

const SEARCH_WAIT_MS = 300;
const PEOPLE_PANEL_WIDTH = 520;

/** A picker's read: the latest answer wins, an earlier one landing late is dropped. */
function useLatest() {
  const seq = useRef(0);
  return {
    next: () => {
      seq.current += 1;
      return seq.current;
    },
    /** The read now in force, for a next page that belongs to it. */
    current: () => seq.current,
    isLatest: (n: number) => n === seq.current,
    drop: () => {
      seq.current += 1;
    },
  };
}

/** A page of a server list: where the next one starts (null or absent: there is no more). */
type Paged = { nextOffset?: number | null };

/**
 * A server list read a page at a time. `key` names the list (the search, the store, the
 * mode): a new key reads its first page afresh - after `waitMs` - and drops whatever an
 * earlier key had, so a failed search never leaves older options to pick. `more()` reads the
 * next page and adds it (`merge`); a failed next page keeps what was read and says so. Null
 * `key`: nothing is read.
 */
function usePagedRead<A extends Paged>(
  key: string | null,
  read: (offset: number) => Promise<A | null>,
  merge: (was: A, next: A) => A,
  waitMs: number,
  onFailed: (err: unknown) => string | null,
) {
  const [answer, setAnswer] = useState<A | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reads, setReads] = useState(0);
  const latest = useLatest();
  const now = useRef({ read, merge, onFailed, answer });
  now.current = { read, merge, onFailed, answer };

  useEffect(() => {
    setAnswer(null);
    setError(null);
    setLoadingMore(false);
    if (key == null) {
      latest.drop();
      setLoading(false);
      return undefined;
    }
    const n = latest.next();
    setLoading(true);
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => now.current.read(0))
        .then((got) => {
          if (!latest.isLatest(n)) return;
          setLoading(false);
          if (got) setAnswer(got);
          else setError(now.current.onFailed(null));
        })
        .catch((err: unknown) => {
          if (!latest.isLatest(n)) return;
          setLoading(false);
          setError(now.current.onFailed(err));
        });
    }, waitMs);
    return () => {
      clearTimeout(timer);
      latest.drop();
    };
    // `latest` is stable for the life of the picker; `read` is read through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reads]);

  const asking = useRef(false);
  const more = () => {
    const was = now.current.answer;
    const offset = was?.nextOffset;
    if (!was || offset == null || asking.current) return;
    asking.current = true;
    const n = latest.current();
    setLoadingMore(true);
    setError(null);
    Promise.resolve()
      .then(() => now.current.read(offset))
      .then((got) => {
        if (!latest.isLatest(n)) return;
        if (got) setAnswer((prev) => (prev ? now.current.merge(prev, got) : got));
        else setError(now.current.onFailed(null));
      })
      .catch((err: unknown) => {
        if (!latest.isLatest(n)) return;
        setError(now.current.onFailed(err));
      })
      .finally(() => {
        asking.current = false;
        if (latest.isLatest(n)) setLoadingMore(false);
      });
  };

  return {
    answer,
    loading,
    loadingMore,
    error,
    hasMore: answer?.nextOffset != null,
    more,
    retry: () => setReads((r) => r + 1),
  };
}

/* ─────────────────────────────── the store ─────────────────────────────── */

export type StorePickerMode = 'raise' | 'ask' | 'link' | 'move';

const REFUSED_WORD: Record<NonNullable<HiringStoreOption['refused']>, string> = {
  not_active: 'Not active',
  redeploy: 'Redeploy',
  address_incomplete: 'No address',
};

/** Two store ids are the same store, whatever case they arrived in. */
const sameStoreId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * A store as one option: "Gadget Hub Indiranagar (GHI-03)" · city; greyed where a raise would
 * refuse it - or, for a move, where the opening is followed now (`current`).
 */
export const storeOption = (store: HiringStoreOption, tag?: PickerTagView | null, current?: string | null): HiringSelectOption => {
  const words = storeOptionWords(store);
  if (sameStoreId(store.storeId, current)) {
    return {
      value: store.storeId, label: words.label, meta: words.meta, disabled: true, hover: MOVE_WORDS.hereNowHover, right: MOVE_WORDS.hereNow,
    };
  }
  return {
    value: store.storeId,
    label: words.label,
    meta: words.meta,
    tags: tag ? [tag] : undefined,
    disabled: store.refused != null,
    hover: store.refused != null ? store.refusedMessage ?? REFUSED_WORD[store.refused] : null,
    right: store.refused != null ? REFUSED_WORD[store.refused] ?? null : null,
  };
};

const readStores = (projectId: number, mode: StorePickerMode, q: string, offset: number) => {
  const text = q.trim() || undefined;
  if (mode === 'ask') return hiringLinksAPI.requestStores(projectId, text, offset || undefined);
  // A move reads the stores within reach behind the manage guard, as the Link window does: its
  // switch is the project's Cancel switch, not Raise's. The move's review is the judge of a store.
  if (mode === 'link' || mode === 'move') return hiringLinksAPI.linkStores(projectId, text, offset || undefined);
  return hiringLinksAPI.raiseStores(projectId, text, offset || undefined);
};

const mergeStores = (was: HiringStoreSearch, next: HiringStoreSearch): HiringStoreSearch => {
  const seen = new Set(was.stores.map((s) => s.storeId));
  return { ...next, stores: [...was.stores, ...next.stores.filter((s) => !seen.has(s.storeId))] };
};

export function StorePicker({
  projectId, mode, value, onChange, onRefused, disabled = false, tag = null, label = RAISE_FORM.store, current = null,
}: {
  projectId: number;
  mode: StorePickerMode;
  value: HiringStoreOption | null;
  onChange: (store: HiringStoreOption | null) => void;
  onRefused?: (err: unknown) => boolean;
  disabled?: boolean;
  /** A tag on the chosen store ("Their store"). */
  tag?: PickerTagView | null;
  label?: string;
  /** Mode 'move': the store the opening is followed for now. It is listed greyed, and cannot be picked. */
  current?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const refused = useRef(onRefused);
  refused.current = onRefused;
  const q = typed.trim();
  // Read while the list is open: the first page at once, a search after a short pause.
  const list = usePagedRead<HiringStoreSearch>(
    open ? `${projectId}|${mode}|${q}` : null,
    async (offset) => {
      const got = (await readStores(projectId, mode, q, offset))?.data?.data;
      return got && Array.isArray(got.stores) ? got : null;
    },
    mergeStores,
    q ? SEARCH_WAIT_MS : 0,
    (err) => (err != null && refused.current?.(err) ? null : raiseErrorText(err, RAISE_FORM.loadFailed)),
  );
  const answer = list.answer;

  const moving = mode === 'move';
  const options = useMemo(() => {
    const stores = (answer?.stores ?? []).map((store) => storeOption(store, null, moving ? current : null));
    // "No store" is the first choice, while nothing is typed. A move always goes to a store.
    return q || moving ? stores : [{ value: '', label: RAISE_FORM.noStore }, ...stores];
  }, [answer, q, moving, current]);
  const chosen = value ? storeOption(value, tag) : null;

  return (
    <HiringSelect
      label={label}
      value={value?.storeId ?? ''}
      selected={chosen}
      onChange={(storeId) => {
        if (!storeId) {
          onChange(null);
          return;
        }
        const store = answer?.stores.find((s) => s.storeId === storeId) ?? null;
        if (store && !(moving && sameStoreId(store.storeId, current))) onChange(store);
      }}
      options={options}
      placeholder={moving ? MOVE_WORDS.storePlaceholder : RAISE_FORM.noStore}
      searchPlaceholder={RAISE_FORM.storeSearch}
      emptyText={RAISE_FORM.storesNone}
      filter="none"
      query={typed}
      onQuery={setTyped}
      onOpenChange={setOpen}
      loading={list.loading}
      error={list.error}
      footer={storesFooter(answer)}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onReachEnd={list.more}
      clearable
      clearLabel={RAISE_FORM.clear(label)}
      disabled={disabled}
      minPanelWidth={360}
    />
  );
}

/* ─────────────────────────────── the person replaced ─────────────────────────────── */

/** A person as one option: name · code · designation · store, and one tag (or why they cannot be picked). */
export const personOption = (person: HiringPersonOption, grouped: boolean): HiringSelectOption => {
  const blocked = personBlockedOf(person);
  const tag = personTagOf(person);
  return {
    value: person.empCode,
    label: personName(person),
    meta: personMeta(person),
    rightTag: blocked ? null : tag,
    right: blocked ? blocked.text : null,
    disabled: !!blocked,
    hover: blocked ? blocked.hover : tag?.hover ?? null,
    group: grouped ? (person.group === 'store' ? 'At this store' : person.group === 'other' ? 'Everyone else' : 'Leaving or left') : null,
  };
};

const mergePeople = (was: HiringPeopleSearch, next: HiringPeopleSearch): HiringPeopleSearch => {
  const seen = new Set(was.people.map((p) => p.empCode));
  return { ...next, preselected: was.preselected, people: [...was.people, ...next.people.filter((p) => !seen.has(p.empCode))] };
};

export function PersonPicker({
  projectId, asking, storeId, value, onChange, onLeaving, onRefused, disabled = false, invalid = false, title,
}: {
  projectId: number;
  /** Ask mode: read with the calls a person who asks may make. */
  asking: boolean;
  /**
   * The chosen store, when one is known before the person is (a store's row, a store picked by
   * hand, a hiring request for a store): "Who" lists that store's people ONLY - deployed there
   * now, or left it in the last 30 days - and its search looks within them (the server's rule,
   * 15 Oct 2026). Null or absent: the whole project.
   */
  storeId?: string | null;
  value: HiringPersonOption | null;
  onChange: (person: HiringPersonOption | null) => void;
  /** The Leaving list as it was read (once, when the picker appears): the owner may open with its `preselected`. */
  onLeaving?: (answer: HiringPeopleSearch) => void;
  onRefused?: (err: unknown) => boolean;
  disabled?: boolean;
  invalid?: boolean;
  title?: string;
}) {
  const [typed, setTyped] = useState('');
  const owner = useRef({ onLeaving, onRefused });
  owner.current = { onLeaving, onRefused };
  const q = typed.trim();
  const searching = q.length >= 2;
  const atStore = !!storeId;
  // Nothing typed (or one letter): a store's people, or with no store the Leaving list and then
  // everyone else - read as soon as the picker appears, so the store's one leaver can open
  // chosen. Typed: the search (within the store's people, for a store), after a pause.
  const list = usePagedRead<HiringPeopleSearch>(
    `${projectId}|${asking}|${storeId ?? ''}|${searching ? q : ''}`,
    async (offset) => {
      const query = { ...(searching ? { q } : {}), storeId: storeId ?? undefined, ...(offset ? { offset } : {}) };
      const got = (await (asking ? hiringLinksAPI.requestPeopleSearch(projectId, query) : hiringLinksAPI.raisePeopleSearch(projectId, query)))?.data?.data;
      return got && Array.isArray(got.people) ? got : null;
    },
    mergePeople,
    searching ? SEARCH_WAIT_MS : 0,
    (err) => (err != null && owner.current.onRefused?.(err) ? null : raiseErrorText(err, RAISE_WORDS.whoReplacedFailed)),
  );
  const answer = list.answer;
  // The Leaving list, told to the owner once per store: its `preselected` opens chosen.
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (!answer || searching || answer.offset) return;
    const key = `${projectId}|${asking}|${storeId ?? ''}`;
    if (told.current === key) return;
    told.current = key;
    owner.current.onLeaving?.(answer);
  }, [answer, searching, projectId, asking, storeId]);

  const options = useMemo(() => {
    const people = answer?.people ?? [];
    const grouped = !searching && new Set(people.map((p) => p.group)).size > 1;
    return people.map((person) => personOption(person, grouped));
  }, [answer, searching]);
  const chosen = value ? personOption(value, false) : null;
  const footer = q.length === 1 ? RAISE_FORM.typeTwo : peopleFooter(answer, typed, atStore);
  const partial = answer && answer.oneOffice !== 'read' ? ' · 1Office could not be read in full' : '';

  return (
    <div className="space-y-1">
      <HiringSelect
        label={RAISE_FORM.who}
        value={value?.empCode ?? ''}
        selected={chosen ? { ...chosen, right: null, rightTag: null } : null}
        onChange={(empCode) => {
          if (!empCode) {
            onChange(null);
            return;
          }
          const person = answer?.people.find((p) => p.empCode === empCode) ?? null;
          if (person && person.pickable !== false) onChange(person);
        }}
        options={options}
        placeholder={RAISE_FORM.whoPlaceholder}
        searchPlaceholder={RAISE_FORM.whoSearch}
        emptyText={peopleEmptyText(searching, atStore)}
        filter="none"
        query={typed}
        onQuery={setTyped}
        loading={list.loading}
        error={list.error}
        footer={footer ? `${footer}${partial}` : null}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onReachEnd={list.more}
        disabled={disabled}
        invalid={invalid}
        title={title}
        minPanelWidth={PEOPLE_PANEL_WIDTH}
      />
      {list.error && !answer && !searching && (
        <button type="button" className="text-xs font-medium text-primary-600 hover:underline" onClick={list.retry}>
          {RAISE_WORDS.tryAgain}
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────── the budget ─────────────────────────────── */

export const budgetOption = (budget: HiringRaiseBudget, extra: { theirs?: boolean; withStore?: boolean } = {}): HiringSelectOption => {
  const words = budgetOptionWords(budget, extra);
  return {
    value: String(budget.budgetId),
    label: budget.budgetName,
    tags: words.tags,
    right: words.right,
    disabled: words.full,
    hover: words.full ? RAISE_FORM.fullHover : null,
  };
};

export function BudgetSelect({
  budgets, error, onRetry, value, onChange, ownBudgetId = null, withStore, disabled = false,
}: {
  /** As the server answered them; null while they are read. */
  budgets: readonly HiringRaiseBudget[] | null;
  error: string | null;
  onRetry: () => void;
  value: string;
  onChange: (budgetId: string) => void;
  /** The replaced person's own budget: first, tagged "Their budget". */
  ownBudgetId?: number | null;
  /** A store is chosen: Store-level budgets are tagged. */
  withStore: boolean;
  disabled?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const ordered = useMemo(() => {
    const list = budgets ?? [];
    const own = ownBudgetId == null ? undefined : list.find((b) => b.budgetId === ownBudgetId);
    return own ? [own, ...list.filter((b) => b !== own)] : [...list];
  }, [budgets, ownBudgetId]);
  const all = useMemo(
    () => ordered.map((budget) => budgetOption(budget, { theirs: budget.budgetId === ownBudgetId, withStore })),
    [ordered, ownBudgetId, withStore],
  );
  const q = typed.trim().toLowerCase();
  // Every budget that matches: nothing is cut off, the list scrolls.
  const matching = q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;

  if (error) {
    return (
      <div className="space-y-1">
        <p role="alert" className="text-sm text-red-700">{error}</p>
        <button type="button" className="text-xs font-medium text-primary-600 hover:underline" onClick={onRetry}>{RAISE_WORDS.tryAgain}</button>
      </div>
    );
  }
  if (budgets && budgets.length === 0) return <p className="pt-2 text-sm text-gray-600">{RAISE_FORM.noBudgets}</p>;
  return (
    <HiringSelect
      label={RAISE_FORM.budget}
      value={value}
      selected={all.find((o) => o.value === value) ?? null}
      onChange={(budgetId) => onChange(budgetId)}
      options={matching}
      placeholder={budgets ? RAISE_FORM.budgetPlaceholder : RAISE_WORDS.budgetsLoading}
      searchPlaceholder={RAISE_FORM.budgetSearch}
      emptyText={RAISE_WORDS.budgetSearchNone}
      filter="none"
      query={typed}
      onQuery={setTyped}
      footer={RAISE_FORM.budgetsShown(matching.length, matching.some((o) => o.disabled))}
      disabled={disabled || !budgets}
    />
  );
}

/* ─────────────────────────────── the city ─────────────────────────────── */

export interface PickedCity { cityId: number; label: string; stateName?: string | null }

const cityOption = (city: HiringRaiseCity): HiringSelectOption => ({
  value: String(city.cityId),
  label: city.cityName,
  meta: city.stateName || null,
  rightTag: city.hasMapLocation ? null : { text: 'No map point', tone: 'grey', hover: RAISE_WORDS.cityNoMap },
});

export function CitySelect({
  projectId, asking, label = RAISE_FORM.city, value, onChange, onRefused, disabled = false, search,
}: {
  projectId: number;
  asking: boolean;
  label?: string;
  value: PickedCity | null;
  onChange: (city: PickedCity | null) => void;
  onRefused?: (err: unknown) => boolean;
  disabled?: boolean;
  /** Where the cities are searched, when not the Raise window's own call (a move's: behind its own guard). */
  search?: (q: string) => Promise<HiringRaiseCity[]>;
}) {
  const [typed, setTyped] = useState('');
  const [cities, setCities] = useState<HiringRaiseCity[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useLatest();
  const refused = useRef(onRefused);
  refused.current = onRefused;
  const searchNow = useRef(search);
  searchNow.current = search;
  const q = typed.trim();

  useEffect(() => {
    setError(null);
    if (q.length < 2) {
      latest.drop();
      setCities(null);
      setLoading(false);
      return undefined;
    }
    const n = latest.next();
    setLoading(true);
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(async () => {
          const own = searchNow.current;
          if (own) return own(q);
          const res = await (asking ? hiringLinksAPI.requestCities(projectId, q) : hiringLinksAPI.raiseCities(projectId, q));
          return res?.data?.data?.cities ?? [];
        })
        .then((got) => {
          if (!latest.isLatest(n)) return;
          setCities(Array.isArray(got) ? got : []);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (!latest.isLatest(n)) return;
          setLoading(false);
          // A failed search leaves nothing older to pick.
          setCities(null);
          if (refused.current?.(err)) return;
          setError(raiseErrorText(err, RAISE_WORDS.citiesFailed));
        });
    }, SEARCH_WAIT_MS);
    return () => {
      clearTimeout(timer);
      latest.drop();
    };
    // `latest` is stable for the life of the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, asking, q]);

  const options = (cities ?? []).map(cityOption);
  const chosen: HiringSelectOption | null = value ? { value: String(value.cityId), label: value.label, meta: value.stateName ?? null } : null;
  return (
    <HiringSelect
      label={label}
      value={value ? String(value.cityId) : ''}
      selected={chosen}
      onChange={(cityId) => {
        const city = (cities ?? []).find((c) => String(c.cityId) === cityId);
        onChange(city ? { cityId: city.cityId, label: city.cityName, stateName: city.stateName } : null);
      }}
      options={options}
      placeholder={RAISE_FORM.cityPlaceholder}
      searchPlaceholder={RAISE_FORM.citySearch}
      emptyText={q.length < 2 ? RAISE_FORM.typeTwo : RAISE_WORDS.noCities}
      filter="none"
      query={typed}
      onQuery={setTyped}
      loading={loading}
      error={error}
      footer={options.length > 0 ? RAISE_FORM.citiesShown(options.length) : null}
      disabled={disabled}
      minPanelWidth={280}
    />
  );
}
